import React, { useMemo, useState } from "react";
import { Clock } from "lucide-react";
import {
  LibraryPage,
  FilterTabs,
  EmptyState,
  Poster,
  Chips,
  LikeButton,
  ConfirmRemove,
  applyTypeFilter,
  splitGenres,
} from "./LibraryLayout";

const toTime = (iso) => {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

const formatDay = (iso) => {
  const t = toTime(iso);
  return t === null
    ? "Watched"
    : `Watched ${new Date(t).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })}`;
};

// Newest first, grouped under "October 2026" style headings.
const groupByMonth = (items) => {
  const sorted = [...items].sort(
    (a, b) => (toTime(b.watchedAt) ?? 0) - (toTime(a.watchedAt) ?? 0),
  );
  const groups = [];
  sorted.forEach((item) => {
    const t = toTime(item.watchedAt);
    const label =
      t === null
        ? "Earlier"
        : new Date(t).toLocaleDateString(undefined, {
            month: "long",
            year: "numeric",
          });
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  });
  return groups;
};

export default function HistoryView({
  items,
  user,
  isLiked,
  onToggleLike,
  onRemove,
  onBack,
  onSignIn,
  toast,
  overlay,
}) {
  const [filter, setFilter] = useState("all");
  const [busyLike, setBusyLike] = useState(null);

  const visible = useMemo(
    () => applyTypeFilter(items, filter),
    [items, filter],
  );
  const groups = useMemo(() => groupByMonth(visible), [visible]);

  const handleLike = async (item) => {
    setBusyLike(item.title);
    try {
      await onToggleLike(item);
    } finally {
      setBusyLike(null);
    }
  };

  const count = items.length;
  const subtitle =
    count === 0 ? null : `${count} ${count === 1 ? "title" : "titles"} watched`;

  let body;
  if (count === 0) {
    body = user ? (
      <EmptyState
        icon={<Clock size={56} />}
        title="Nothing watched yet"
        text="Mark a recommendation as watched, or like it, and it will show up here."
        actionLabel="Find something to watch"
        onAction={onBack}
      />
    ) : (
      <EmptyState
        icon={<Clock size={56} />}
        title="Sign in to keep a watch history"
        text="Your history is saved to your account so it follows you across devices."
        actionLabel="Sign in"
        onAction={onSignIn}
      />
    );
  } else if (visible.length === 0) {
    body = (
      <EmptyState
        icon={<Clock size={56} />}
        title="No matches"
        text="Nothing in your history matches this filter."
        actionLabel="Show all"
        onAction={() => setFilter("all")}
      />
    );
  } else {
    body = groups.map((g) => (
      <section key={g.label} aria-label={g.label}>
        <h2 className="nq-lib__month">{g.label}</h2>
        <ul className="nq-lib__grid">
          {g.items.map((item) => {
            const liked = isLiked(item.title);
            return (
              <li key={`${item.title}-${item.watchedAt}`} className="nq-card">
                <Poster url={item.posterUrl} type={item.type} />
                <div className="nq-card__body">
                  <h3 className="nq-card__title">{item.title}</h3>
                  <p className="nq-card__meta">
                    {formatDay(item.watchedAt)}
                    {item.type ? ` · ${item.type}` : ""}
                  </p>
                  <Chips items={splitGenres(item.genre)} />
                  <div className="nq-card__actions">
                    <LikeButton
                      liked={liked}
                      disabled={busyLike === item.title}
                      onClick={() => handleLike(item)}
                    />
                    <ConfirmRemove
                      label={item.title}
                      onConfirm={() => onRemove(item)}
                    />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    ));
  }

  return (
    <LibraryPage
      title="Watch history"
      subtitle={subtitle}
      onBack={onBack}
      toolbar={
        count > 1 ? <FilterTabs value={filter} onChange={setFilter} /> : null
      }
      toast={toast}
      overlay={overlay}
    >
      {body}
    </LibraryPage>
  );
}
